/**
 * Allow-list logger.
 *
 * ADR 0001: "Editor storage and logs contain no PHI." A deny-list is not
 * sufficient — a field nobody anticipated is exactly the field that leaks. So
 * only explicitly permitted keys are emitted, and every other value is replaced
 * with a redaction marker that still records that something was dropped.
 */

export const REDACTED = "[redacted]";

/**
 * Keys safe to emit: identifiers, machine-readable state, and timings. No
 * free-text field, no name, no clinical content, and no value sourced from user
 * input appears here.
 */
const ALLOWED_KEYS = new Set([
  "appVersion",
  "candidateCount",
  "capability",
  "code",
  "commit",
  "component",
  "count",
  "durationMs",
  "environment",
  "errorName",
  "event",
  "issuer",
  "method",
  "operation",
  "path",
  "projectId",
  "reason",
  "requestId",
  "route",
  "schemaVersion",
  "section",
  "status",
  "tenantId",
]);

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogFields = Record<string, unknown>;

/** Values that are safe to emit verbatim once their key is allow-listed. */
function isPrimitive(value: unknown): boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  );
}

export function redact(fields: LogFields): LogFields {
  const output: LogFields = {};
  for (const [key, value] of Object.entries(fields)) {
    if (!ALLOWED_KEYS.has(key)) {
      output[key] = REDACTED;
      continue;
    }
    output[key] = isPrimitive(value) ? value : REDACTED;
  }
  return output;
}

type Sink = (level: LogLevel, message: string, fields: LogFields) => void;

const consoleSink: Sink = (level, message, fields) => {
  const method = level === "debug" ? "log" : level;
  console[method](message, fields);
};

let sink: Sink = consoleSink;

/** Test seam. Production always uses the console sink. */
export function setLogSink(next: Sink | null): void {
  sink = next ?? consoleSink;
}

function emit(level: LogLevel, message: string, fields: LogFields = {}): void {
  sink(level, message, redact(fields));
}

export const logger = {
  debug: (message: string, fields?: LogFields) => emit("debug", message, fields),
  info: (message: string, fields?: LogFields) => emit("info", message, fields),
  warn: (message: string, fields?: LogFields) => emit("warn", message, fields),
  error: (message: string, fields?: LogFields) => emit("error", message, fields),
};
